/**
 * Well-known Solana program IDs and their human-readable names.
 */
export const KNOWN_PROGRAMS: Record<string, string> = {
  '11111111111111111111111111111111': 'System Program',
  'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA': 'Token Program',
  'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb': 'Token-2022',
  'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL': 'Associated Token Account',
  'metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s': 'Token Metadata',
  'ComputeBudget111111111111111111111111111111': 'Compute Budget',
  'Vote111111111111111111111111111111111111111': 'Vote Program',
  'Stake11111111111111111111111111111111111111': 'Stake Program',
  'MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr': 'Memo Program',
  'Memo1UhkJBfCR6MNLHngHP3QUhC3Gxi5QpA94YWVdrm': 'Memo Program (v1)',
  'JUP6LkbZbjS1jKKwapdHNy74zcZ3tLUZoi5QNyVTaV4': 'Jupiter v6',
};

/**
 * Known System Program instruction types (by first 4 bytes of data).
 */
export const SYSTEM_INSTRUCTIONS: Record<number, string> = {
  0: 'CreateAccount',
  1: 'Assign',
  2: 'Transfer',
  3: 'CreateAccountWithSeed',
  4: 'AdvanceNonceAccount',
  5: 'WithdrawNonceAccount',
  6: 'InitializeNonceAccount',
  7: 'AuthorizeNonceAccount',
  8: 'Allocate',
  9: 'AllocateWithSeed',
  10: 'AssignWithSeed',
  11: 'TransferWithSeed',
  12: 'UpgradeNonceAccount',
};

/**
 * Known Token Program instruction types.
 */
export const TOKEN_INSTRUCTIONS: Record<number, string> = {
  0: 'InitializeMint',
  1: 'InitializeAccount',
  3: 'Transfer',
  4: 'Approve',
  5: 'Revoke',
  7: 'MintTo',
  8: 'Burn',
  9: 'CloseAccount',
  12: 'TransferChecked',
  13: 'ApproveChecked',
};

/**
 * Decode a base58 string to Uint8Array.
 * Used for Solana public keys and signatures.
 */
const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

export function base58Decode(str: string): Uint8Array {
  // bytes stores the result in big-endian order
  const bytes: number[] = [0];

  for (const char of str) {
    let carry = BASE58_ALPHABET.indexOf(char);
    if (carry === -1) throw new Error(`Invalid base58 character: ${char}`);

    for (let j = 0; j < bytes.length; j++) {
      carry += (bytes[j] ?? 0) * 58;
      bytes[j] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }

  // Handle leading '1's (which map to leading zero bytes)
  let leadingZeros = 0;
  for (const char of str) {
    if (char === '1') leadingZeros++;
    else break;
  }

  // Strip trailing zeros from bytes (they are leading zeros in big-endian)
  // but keep the actual value bytes
  let bytesLen = bytes.length;
  while (bytesLen > 1 && bytes[bytesLen - 1] === 0) {
    bytesLen--;
  }
  // Special case: if the entire value is 0, bytesLen is 1 with bytes[0]=0.
  // In that case, the leading zeros from '1' chars already cover it,
  // so we skip the value bytes entirely.
  if (bytesLen === 1 && bytes[0] === 0) {
    bytesLen = 0;
  }

  // bytes is in LSB-first order, reverse to get big-endian + prepend leading zeros
  const result = new Uint8Array(leadingZeros + bytesLen);
  for (let i = 0; i < bytesLen; i++) {
    result[leadingZeros + bytesLen - 1 - i] = bytes[i] ?? 0;
  }

  return result;
}

/**
 * Encode Uint8Array to base58 string.
 */
export function base58Encode(bytes: Uint8Array): string {
  const digits: number[] = [0];

  for (let i = 0; i < bytes.length; i++) {
    let carry = bytes[i] ?? 0;
    for (let j = 0; j < digits.length; j++) {
      carry += (digits[j] ?? 0) << 8;
      digits[j] = carry % 58;
      carry = Math.floor(carry / 58);
    }
    while (carry > 0) {
      digits.push(carry % 58);
      carry = Math.floor(carry / 58);
    }
  }

  // Handle leading zeros
  let leadingZeros = '';
  for (let i = 0; i < bytes.length; i++) {
    if (bytes[i] === 0) leadingZeros += '1';
    else break;
  }

  // Strip leading zeros from digits (they are trailing after reverse)
  // If the entire value is zero, digits is [0] — skip it since
  // leadingZeros already covers all the zero bytes.
  while (digits.length > 1 && digits[digits.length - 1] === 0) {
    digits.pop();
  }
  if (digits.length === 1 && digits[0] === 0 && leadingZeros.length > 0) {
    return leadingZeros;
  }

  return leadingZeros + digits.reverse().map((d) => BASE58_ALPHABET[d]).join('');
}
